const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

interface RequestOptions extends RequestInit {
  _retry?: boolean
}

interface ApiResponse<T = any> {
  data: T
  status: number
  ok: boolean
}

class ApiError extends Error {
  response?: {
    status: number
    data: any
  }

  constructor(message: string, status?: number, data?: any) {
    super(message)
    this.name = 'ApiError'
    if (status !== undefined) {
      this.response = { status, data }
    }
  }
}

async function refreshTokens(): Promise<boolean> {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) return false

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    })

    if (!response.ok) return false

    const data = await response.json()
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)
    return true
  } catch {
    return false
  }
}

async function request<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('access_token')

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>
  }

  // For FormData (file uploads) let the browser set multipart/form-data +
  // boundary itself — forcing application/json breaks parsing on the server.
  if (options.body instanceof FormData) {
    delete headers['Content-Type']
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`

  try {
    const response = await fetch(url, {
      ...options,
      headers
    })

    // Handle 401 - try to refresh token
    if (response.status === 401 && !options._retry) {
      const refreshed = await refreshTokens()
      if (refreshed) {
        return request(endpoint, { ...options, _retry: true })
      }

      // If impersonating, restore admin session instead of forcing login
      const stashed = localStorage.getItem('impersonator_session')
      if (stashed) {
        try {
          const adminSession = JSON.parse(stashed)
          localStorage.setItem('access_token', adminSession.access_token)
          localStorage.setItem('refresh_token', adminSession.refresh_token)
          localStorage.removeItem('impersonator_session')
          localStorage.removeItem('impersonation_audit_id')
          localStorage.removeItem('impersonation_expires_at')
          window.location.href = '/master'
          throw new ApiError('Impersonação expirada — sessão admin restaurada', 401)
        } catch {
          // Fall through to normal logout if parse fails
        }
      }

      // Refresh failed - clear tokens and redirect
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      window.location.href = '/login'
      throw new ApiError('Sessão expirada', 401)
    }

    let data: any = null
    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      data = await response.json()
    } else {
      data = await response.text()
    }

    if (!response.ok) {
      throw new ApiError(
        data?.error || data?.message || 'Request failed',
        response.status,
        data
      )
    }

    return {
      data,
      status: response.status,
      ok: response.ok
    }
  } catch (error) {
    if (error instanceof ApiError) throw error
    throw new ApiError((error as Error).message)
  }
}

// FormData must be passed through untouched (JSON.stringify would turn it into
// "{}"); everything else is JSON-encoded.
function encodeBody(body?: any): BodyInit | undefined {
  if (body == null) return undefined
  if (body instanceof FormData) return body
  return JSON.stringify(body)
}

export const api = {
  get: <T = any>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: encodeBody(body)
    }),

  put: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: encodeBody(body)
    }),

  patch: <T = any>(endpoint: string, body?: any, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: encodeBody(body)
    }),

  delete: <T = any>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' })
}

export default api
